import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { USER_MANAGEMENT_API_CONFIG } from '../config';
import { BillingStatus, InvoiceHistoryPage, InvoiceStatusFilter, SubscriptionPlan } from '../models';

/**
 * Service for subscription plan discovery and Stripe billing operations.
 *
 * All HTTP calls use the USER_MANAGEMENT_API_CONFIG base URL.
 * The auth interceptor in the consuming app automatically attaches Bearer
 * tokens to requests matching the CMS API base URL.
 *
 * @remarks
 * `getPublicPlans()` is unauthenticated — the backend serves it publicly.
 * All other methods require an authenticated admin user.
 */
@Injectable({
  providedIn: 'root',
})
export class BillingService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl: string;

  constructor() {
    const config = inject(USER_MANAGEMENT_API_CONFIG);
    this._apiUrl = config.apiBaseUrl;
  }

  /**
   * Fetches all active subscription plans (public endpoint, no auth required).
   * Returns plans ordered by `displayOrder` ascending.
   */
  getPublicPlans(): Observable<SubscriptionPlan[]> {
    return this._http
      .get<{ success: boolean; data: SubscriptionPlan[] }>(
        `${this._apiUrl}/api/subscription-plans/public`,
      )
      .pipe(
        map(res => res.data),
        catchError(this._handleError('getPublicPlans')),
      );
  }

  /**
   * Creates a Stripe Checkout session for upgrading the org's subscription.
   * Returns a redirect URL to the Stripe-hosted checkout page.
   *
   * @param planSlug      Target plan slug, e.g. 'pro'
   * @param billingCycle  'monthly' or 'annual'
   */
  createCheckoutSession(
    planSlug: string,
    billingCycle: 'monthly' | 'annual',
  ): Observable<{ checkoutUrl: string }> {
    return this._http
      .post<{ success: boolean; data: { checkoutUrl: string } }>(
        `${this._apiUrl}/api/billing/checkout`,
        { planSlug, billingCycle },
      )
      .pipe(
        map(res => res.data),
        catchError(this._handleError('createCheckoutSession')),
      );
  }

  /**
   * Creates a Stripe Customer Portal session for managing the existing
   * subscription (cancel, update payment method, view invoices).
   */
  createPortalSession(): Observable<{ portalUrl: string }> {
    return this._http
      .post<{ success: boolean; data: { portalUrl: string } }>(
        `${this._apiUrl}/api/billing/portal`,
        {},
      )
      .pipe(
        map(res => res.data),
        catchError(this._handleError('createPortalSession')),
      );
  }

  /**
   * Asks the backend to re-derive and write the org's current subscription
   * into Firebase custom claims from the database (idempotent).
   * Call this after a successful Stripe checkout redirect so claims are
   * guaranteed to be fresh before the client force-refreshes its token.
   */
  syncClaims(): Observable<void> {
    return this._http
      .post<{ success: boolean }>(`${this._apiUrl}/api/auth/set-custom-claims`, {})
      .pipe(
        map(() => void 0),
        catchError(this._handleError('syncClaims')),
      );
  }

  /**
   * Fetches the current billing status for an organization.
   *
   * @param orgId MongoDB _id of the organization
   */
  getOrgBillingStatus(orgId: string): Observable<BillingStatus> {
    return this._http
      .get<{ success: boolean; data: BillingStatus }>(
        `${this._apiUrl}/api/organizations/${orgId}/billing`,
      )
      .pipe(
        map(res => res.data),
        catchError(this._handleError('getOrgBillingStatus')),
      );
  }

  /**
   * Fetches a page of Stripe invoices for an organization.
   * Pagination is cursor-based: pass `opts.startingAfter` from the previous
   * page's `nextCursor` to load the next page.
   * Errors are propagated raw — callers handle 422 (no paid history) separately.
   *
   * @param orgId  MongoDB _id of the organization
   * @param opts   Optional limit, cursor, and status filter
   */
  getInvoices(
    orgId: string,
    opts: {
      limit?: number;
      startingAfter?: string;
      status?: Exclude<InvoiceStatusFilter, 'all'>;
    } = {},
  ): Observable<InvoiceHistoryPage> {
    let params = new HttpParams();
    if (opts.limit != null) params = params.set('limit', opts.limit);
    if (opts.startingAfter) params = params.set('starting_after', opts.startingAfter);
    if (opts.status) params = params.set('status', opts.status);

    return this._http
      .get<{ success: boolean; data: InvoiceHistoryPage }>(
        `${this._apiUrl}/api/billing/invoices/${orgId}`,
        { params },
      )
      .pipe(
        map(res => res.data),
        catchError(this._handleError('getInvoices')),
      );
  }

  private _handleError(operation: string) {
    return (error: HttpErrorResponse): Observable<never> => {
      console.error(`[BillingService] ${operation} failed:`, error);
      return throwError(() => error);
    };
  }
}
